import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Section from '@components/Section';
import { fetchMoreSearchResults, selectSearch } from '@actions/search';
import { CircledCheckIcon, CircledXIcon, LoaderIcon } from '@components/Icons';
import styles from './Search.module.scss';
import { SearchState } from '@app/types';
import ResultsDisplay from './ResultsDisplay';
import { useAppContactSelecting } from '@hooks/use-app-contact-selecting';
import { selectSelectedContact } from '@actions/ui';
import { useScrollRestoration } from '@hooks/use-scroll-restoration';
import { NoResultsFound } from './EmptySearch';
import { selectNavBarState } from '@reducers/ui/navBar';

const SearchTitle = ({ search }: { search: SearchState }) => {
  const { status: navBarStatus } = useSelector(selectNavBarState);
  const { totalResults, isFetching } = search.results;
  const forTerm = ` for “${search.term}”`;
  const forSuffix = navBarStatus === 'pinned' ? '' : forTerm;
  let text = `${totalResults} Matches Found${forSuffix}`;
  let Icon = CircledCheckIcon;

  if (search.term.length === 0) {
    return <div className={styles.resultsHeaderContainer}>Search</div>;
  }

  if (isFetching) {
    if (search.results.page == 0) {
      text = `Searching ${forTerm}`;
    } else {
      text = `Loading more matches${forTerm}`;
    }

    Icon = ({ className }) => <LoaderIcon className={className} width={12} height={12} />;
  } else if (totalResults === 0) {
    text = `No Matches Found${forSuffix}`;

    Icon = CircledXIcon;
  } else if (totalResults === 1) {
    text = `1 Match Found${forSuffix}`;
  }

  return (
    <div className={styles.resultsHeaderContainer}>
      Search
      <Section.Title className={styles.resultsHeader} IconComponent={Icon}>
        {text}
      </Section.Title>
    </div>
  );
};

export default function Search() {
  const dispatch = useDispatch();
  const scrollingContainerRef = useRef();
  const search = useSelector(selectSearch);
  const selectedContact = useSelector(selectSelectedContact);
  const [highlightedItem, setHighlightedItem] = useState(selectedContact);
  const loadNextPage = useCallback(() => dispatch(fetchMoreSearchResults()), []);
  useScrollRestoration({ viewID: 'Search', scrollingContainerRef });

  useAppContactSelecting({
    contact: search.term.length > 0 && highlightedItem,
    source: 'search',
    additionalDeps: [search.term],
  });

  useEffect(() => {
    if (search.term.length === 0) {
      setHighlightedItem(null);
    }
  }, [search.term]);

  return (
    <ResultsDisplay
      search={search}
      setHighlightedItem={setHighlightedItem}
      highlightedItem={selectedContact}
      loadNextPage={loadNextPage}
      title={<SearchTitle search={search} />}
      ref={scrollingContainerRef}
      resetListHighlightOnSearch
      emptyStateUI={<NoResultsFound />}
    />
  );
}
